import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HandHeart, Check, Plus } from "lucide-react";

const SUGGESTIONS = [
  "Go to the gym",
  "Clean one counter",
  "Take a 10-minute walk",
  "Send one message",
  "Work on business for 20 minutes",
  "No nap before noon",
  "Make one sales/business move",
];

export default function TrustDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [custom, setCustom] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => setDeposits(await api.listTrustDeposits());
  useEffect(() => { load(); }, []);

  const todayDeposit = deposits.find((d) => d.date === today);
  const history = deposits.filter((d) => d.date !== today);

  const choose = async (promise) => {
    await api.createTrustDeposit(promise);
    setCustom("");
    toast.success("Promise set. Keep it small, keep it real.");
    load();
  };

  const complete = async (id) => {
    await api.completeTrustDeposit(id);
    toast.success("Trust deposited.");
    load();
  };

  return (
    <PageShell title="Trust deposits" subtitle="One small promise. Keep it, build trust with yourself.">
      {todayDeposit ? (
        <div className="bg-zinc-100 rounded-3xl p-6 shadow-md mb-8" data-testid="current-deposit">
          <p className="text-xs font-semibold text-[#365458] uppercase tracking-wide mb-2">Today</p>
          <p className={`font-heading text-2xl font-extrabold text-zinc-900 leading-tight ${todayDeposit.completed ? "line-through opacity-60" : ""}`}>
            {todayDeposit.promise}
          </p>
          {todayDeposit.completed ? (
            <div className="mt-4 inline-flex items-center gap-2 text-[#365458] font-bold">
              <Check className="w-5 h-5" /> Trust deposited.
            </div>
          ) : (
            <Button
              data-testid="complete-deposit-button"
              onClick={() => complete(todayDeposit.id)}
              className="w-full h-14 mt-4 rounded-2xl bg-[#365458] hover:bg-[#2A4346] text-white text-base font-bold"
            >
              <HandHeart className="w-5 h-5 mr-2" /> I kept it
            </Button>
          )}
          <button
            data-testid="change-deposit-button"
            onClick={() => api.deleteTrustDeposit(todayDeposit.id).then(load)}
            className="w-full text-center text-xs text-zinc-500 mt-3 hover:text-zinc-700"
          >
            Choose a different promise
          </button>
        </div>
      ) : (
        <div className="mb-8">
          <p className="text-zinc-400 text-sm font-semibold uppercase tracking-wide mb-3">Pick one for today</p>
          <div className="space-y-2.5 mb-4" data-testid="suggestion-list">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid={`suggestion-${s}`}
                onClick={() => choose(s)}
                className="w-full text-left bg-zinc-100 hover:bg-white rounded-2xl px-5 py-4 text-zinc-900 font-semibold shadow-md transition-colors active:scale-[0.99]"
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="custom-deposit-input"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Or write your own…"
              className="bg-zinc-800 border-zinc-700 text-zinc-100 h-12 rounded-xl"
            />
            <Button
              data-testid="custom-deposit-button"
              onClick={() => custom.trim() && choose(custom.trim())}
              className="h-12 px-4 rounded-xl bg-[#365458] hover:bg-[#2A4346]"
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <section>
          <h2 className="text-zinc-400 text-sm font-semibold uppercase tracking-wide mb-3">Kept promises</h2>
          <div className="space-y-2" data-testid="deposit-history">
            {history.map((d) => (
              <div key={d.id} className="flex items-center gap-3 bg-zinc-800/50 rounded-xl px-4 py-3" data-testid={`history-item-${d.id}`}>
                {d.completed ? (
                  <Check className="w-4 h-4 text-[#7FB0B5] shrink-0" />
                ) : (
                  <span className="w-4 h-4 rounded-full border border-zinc-600 shrink-0" />
                )}
                <span className="text-zinc-300 text-sm flex-1">{d.promise}</span>
                <span className="text-zinc-600 text-xs">{d.date}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </PageShell>
  );
}
